import React, { ReactElement, useEffect, useLayoutEffect } from 'react'
import { RouteComponentProps } from '@reach/router'
import DashboardContainer from 'components/Dashboard/DashboardContainer'
import DashboardHeader from 'components/Dashboard/DashboardHeader'
import ProvisionIcon from 'images/dashboard/navBar/provision.svg'
import { Flex } from 'theme-ui'
import AddButton from 'ui/AddButton'
import ProvisionContent from './ProvisionContent'
import { useGetProvisionQuery } from 'store/api/provision'
import NoResults from 'components/Dashboard/CommonComponents/NoResults'
import { Button } from 'ui'
import ProvisionTrayOrModalDialog from './ProvisionTrayOrModalDialog'
import useQueryString from 'utils/useQueryString'
import EmailProvisioningTrayOrModalDialog from './ProvisionTable/Options/EmailProvisioningTrayOrDialog'
import { useUpdateDocumentTitle } from 'utils/useUpdateDocumentTitle'
import { useGetStatEndpointsQuery } from 'store/api/analytics'
import ProvisionPlaceholder from './Placeholder'
import { useAppSelector } from 'store/hooks'
import omit from 'lodash/omit'
import useBarryController from 'components/Barry/hooks/useBarryController '

export enum ProvisionDialogType {
  ADD = 'add',
  EMAIL = 'email',
  INSTRUCTIONS = 'instructions',
}

export default function Provision({}: RouteComponentProps): ReactElement {
  const { data: provisionData, isLoading } = useGetProvisionQuery('')
  useUpdateDocumentTitle('Provision')
  useGetStatEndpointsQuery('')
  const { setBarryProvisioningHelpPopupOpen } = useBarryController()

  useEffect(() => {
    setBarryProvisioningHelpPopupOpen(true)
    return () => {
      setBarryProvisioningHelpPopupOpen(false)
    }
  }, [setBarryProvisioningHelpPopupOpen])

  return (
    <>
      <DashboardContainer
        header={
          <DashboardHeader
            icon={ProvisionIcon}
            title="Provision"
            description="Centralized deployment for whole fleets of devices."
            rightContent={<RightHeaderContent />}
          />
        }
        loadingPlaceholder={isLoading && <ProvisionPlaceholder />}
      >
        {provisionData?.provisions.length ? <ProvisionContent /> : <NoResultsContent />}

        <ModalDialogs />
      </DashboardContainer>
    </>
  )
}

function RightHeaderContent() {
  const { nav, qs } = useQueryString()

  return (
    <Flex sx={{ gap: '1.6rem', alignItems: 'center' }}>
      <AddButton
        dataTestId="add-provision-button"
        ariaLabel="add provision button"
        onClick={() => nav({ ...qs, provisionDialog: ProvisionDialogType.ADD })}
      />
    </Flex>
  )
}

function NoResultsContent() {
  const { nav, qs } = useQueryString()

  return (
    <NoResults
      message="You do not have any provisioning codes."
      content={
        <Button
          ariaLabel="create provisioning code"
          variant="newPrimary"
          sx={{
            m: 0,
            borderRadius: '45px',
            fontSize: '1.8rem',
            px: '2.2rem',
            fontWeight: 'bold',
            height: '3.8rem',
          }}
          onClick={() => nav({ ...qs, provisionDialog: ProvisionDialogType.ADD })}
        >
          Create Provisioning Code
        </Button>
      }
    />
  )
}

function ModalDialogs() {
  const { nav, qs } = useQueryString()
  const selectProvisionPk = useAppSelector(s => s.organization.selectProvisionPk || '')

  useLayoutEffect(() => {
    if (qs.provisionDialog === ProvisionDialogType.INSTRUCTIONS && !selectProvisionPk) {
      nav({ ...omit(qs, 'provisionDialog') })
    }
  }, [nav, qs, qs.provisionDialog, selectProvisionPk])

  return (
    <>
      {(qs.provisionDialog === ProvisionDialogType.ADD ||
        (qs.provisionDialog === ProvisionDialogType.INSTRUCTIONS && !!selectProvisionPk)) && (
        <ProvisionTrayOrModalDialog />
      )}
      {qs.provisionDialog === ProvisionDialogType.EMAIL && <EmailProvisioningTrayOrModalDialog />}
    </>
  )
}
